import { ApiRequestError } from '@croissants/contracts';
import { type AppCatalogue, errorSentence, type Locale } from '@croissants/i18n';
import type { JSX, ReactNode } from 'react';
import { Button, type ButtonSize, Card, cx } from './primitives';

/**
 * The card a failed request leaves on a screen, on every portal (F12).
 *
 * Its words are each portal's own - the sentence comes from that app's
 * catalogue through `errorSentence`, the title and the labels likewise - but
 * the SHAPE is one: the title, the sentence, the request id support asks for,
 * whatever the portal adds, and the retry.
 *
 * This file is the one reader of everything a screen takes off a failure: the
 * sentence (`bindErrorSentence`), the request id (`errorRequestId`) and the
 * per-field map (`fieldErrors`) are read HERE and nowhere else. Partner and
 * Internal each read and printed the request id on their own, and the guest's
 * card never printed it at all, so a guest reading an error had nothing to
 * quote back; the field map and the sentence binding were each written twice
 * or four times over, identical but for the catalogue.
 *
 * It refuses to own any WORDS: the catalogue is the app's, bound once, and the
 * order a sentence is chosen in is `errorSentence`'s (`@croissants/i18n`).
 */
export interface ErrorCardProps {
  /** What failed. An `ApiRequestError` contributes its request id. */
  readonly error: unknown;
  /** Already translated. */
  readonly title: ReactNode;
  /** The sentence for this failure, already translated (`errorSentence`). */
  readonly message: ReactNode;
  /** "Request id: {id}" in the portal's words. */
  readonly requestIdLabel: (id: string) => ReactNode;
  readonly onRetry?: () => void;
  readonly retryLabel?: ReactNode;
  /** The retry's step on the button scale; the portal's own default otherwise. */
  readonly retrySize?: ButtonSize;
  /** Buttons before the retry - a portal's own way out (sign in again). */
  readonly actions?: ReactNode;
  /** Lines between the sentence and the request id - a diagnosis. */
  readonly children?: ReactNode;
  readonly className?: string;
  readonly flat?: boolean;
}

/** The sentence a screen shows for a failure, in the reader's language. */
export type ErrorSentenceFor = (error: unknown, locale: Locale) => string;

/**
 * The platform's one error renderer, bound to ONE app catalogue.
 *
 * `errorSentence` takes three arguments - the failure, the locale and the
 * app's catalogue for that locale - and the third is the one a second call
 * site gets wrong. Each portal binds it once, in one line, and passes the
 * locale on every call: there is deliberately no default locale, because a
 * hidden `'en'` is how 24 partner screens showed a French conciergerie
 * English sentences.
 */
export function bindErrorSentence(catalog: {
  readonly catalogues: Readonly<Record<Locale, AppCatalogue>>;
}): ErrorSentenceFor {
  return (error, locale) => errorSentence(error, locale, catalog.catalogues[locale]);
}

/**
 * The boxes a refused write names, keyed by the dotted body path `bind.ts`
 * sends (one entry per zod issue); {} for anything else. `error.validation`
 * tells the reader to "check the highlighted fields", and a form that never
 * reads this has none to check.
 */
export function fieldErrors(error: unknown): Readonly<Record<string, string>> {
  return error instanceof ApiRequestError ? (error.fields ?? {}) : {};
}

/** The request id an error carries, or undefined - the one reader of it. */
export function errorRequestId(error: unknown): string | undefined {
  return error instanceof ApiRequestError ? error.requestId : undefined;
}

export function ErrorCard({
  error,
  title,
  message,
  requestIdLabel,
  onRetry,
  retryLabel,
  retrySize,
  actions,
  children,
  className,
  flat = false,
}: ErrorCardProps): JSX.Element {
  const requestId = errorRequestId(error);
  const retry =
    onRetry === undefined ? null : (
      <Button
        variant="secondary"
        {...(retrySize === undefined ? {} : { size: retrySize })}
        onClick={onRetry}
      >
        {retryLabel}
      </Button>
    );
  return (
    <Card className={cx('cib-error-card', className)} title={title} flat={flat}>
      <p role="alert">{message}</p>
      {children}
      {requestId === undefined ? null : (
        <p className="cib-error-card__request">{requestIdLabel(requestId)}</p>
      )}
      {actions === undefined && retry === null ? null : (
        <div className="cib-error-card__actions">
          {actions}
          {retry}
        </div>
      )}
    </Card>
  );
}
